import ExcelJS, { Alignment, Borders, Fill, Font } from 'exceljs';
import { saveAs } from 'file-saver';
import { RelatorioLaItem } from '../relatorioLa/relatorioLaPage.types';

type ClienteSelecionado = { id: string; value: string };

type Celula = string | number | null;
type Linha = Celula[];

const ALIGN_MIDDLE_LEFT: Partial<Alignment> = { vertical: 'middle', horizontal: 'left' };


const CABECALHO_ITENS = [
    'NCM',
    'Nr. Adição',
    'Nr. Item',
    'Nr. Pedido',
    'Código',
    'Número da Fatura',
    'Número da LI',
    'Descrição',
    'Unidade de Medida',
    'Peso Bruto',
    'Peso Liq.',
    'Quantidade',
    'vl. Unitário R$',
    'FOB R$',
    'FRETE R$',
    'SEGURO R$',
    'Acréscimo R$',
    'Dedução R$',
    'Despesa R$',
    'CIF R$',
    'Aliq. II',
    'II R$',
    'Base IPI',
    'Aliq. IPI',
    'IPI R$',
    'Base PIS/COFINS R$',
    'Aliq. PIS',
    'PIS R$',
    'Aliq. COFINS',
    'COFINS R$',
    'COFINS a Recolher',
    'Tx Siscomex',
    'Base ICMS R$',
    'Aliq. ICMS',
    '% Redução ICMS',
    'Aliq. reduzida ICMS',
    'Valor do ICMS base reduzida',
    'ICMS R$',
    'AFRMM',
] as const;

const FONTE_PADRAO: Partial<Font> = { name: 'Calibri', size: 11, color: { argb: 'FF000000' } };
const FONTE_HEADER: Partial<Font> = { ...FONTE_PADRAO, bold: true };
const PREENCHIMENTO_HEADER: Partial<Fill> = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
const BORDA_PRETA: Partial<Borders> = {
    top: { style: 'thin', color: { argb: 'FF000000' } },
    left: { style: 'thin', color: { argb: 'FF000000' } },
    bottom: { style: 'thin', color: { argb: 'FF000000' } },
    right: { style: 'thin', color: { argb: 'FF000000' } },
};
const ALIGN_MIDDLE: Partial<Alignment> = { vertical: 'middle' };

function normalizarNomeAba(nome: string) {
    if (!nome) return 'Planilha';
    return nome.replace(/[\\\/\?\*\[\]\:]/g, ' ').slice(0, 31);
}

function configurarLargurasMinimas(ws: ExcelJS.Worksheet) {
    const widths = new Array(CABECALHO_ITENS.length).fill(12);
    ws.columns = widths.map((w) => ({ width: w }));
}

function autoFitColumns(ws: ExcelJS.Worksheet, colCount: number, min = 10, max = 120) {
    for (let col = 1; col <= colCount; col++) {
        let maxLen = 0;
        ws.eachRow((row) => {
            const cell = row.getCell(col);
            const v = cell.value as any;
            let text = '';
            if (v === null || v === undefined) {
                text = '';
            } else if (typeof v === 'object' && v.richText) {
                text = v.richText.map((r: any) => r.text).join('');
            } else {
                text = String(v);
            }
            maxLen = Math.max(maxLen, text.length);
        });
        ws.getColumn(col).width = Math.min(Math.max(maxLen + 2, min), max);
    }
}

function mapItemParaLinha(item: any): Linha {
    const reducaoIcms = 0;
    const aliqReduzida = item.aliquota_icms;
    const valorIcmsBaseReduzida = 0;

    return [
        item.ncm ?? null,                          // NCM
        item.adicao ?? null,                       // Nr. Adição
        item.item ?? null,                         // Nr. Item
        item.pedido ?? null,                       // Nr. Pedido
        item.produto ?? null,                      // Código (partnumber)
        item.numero_danfe ?? null,                 // Número da Fatura
        item.nr_li ?? null,                        // Número da LI
        item.descricao ?? null,                    // Descrição
        item.unidade ?? null,                      // Unidade de Medida
        item.peso_bruto ?? null,                   // Peso Bruto
        item.peso_liquido ?? null,                 // Peso Liq.
        item.quantidade ?? null,                   // Quantidade
        item.cif_unitario ?? null,                 // vl. Unitário R$
        item.fob ?? null,                          // FOB R$
        item.frete ?? null,                        // FRETE R$
        item.seguro ?? null,                       // SEGURO R$
        item.acrescimo ?? null,                    // Acréscimo R$
        item.deducao ?? null,                      // Dedução R$
        item.despesa ?? null,                      // Despesa R$
        item.cif_total ?? null,                    // CIF R$
        item.aliquota_ii ?? null,                  // Aliq. II
        item.ii ?? null,                           // II R$
        item.base_calculo_ipi ?? null,             // Base IPI
        item.aliquota_ipi ?? null,                 // Aliq. IPI
        item.ipi ?? null,                          // IPI R$
        item.base_calculo_pis_cofins ?? null,      // Base PIS/COFINS R$
        item.aliquota_pis ?? null,                 // Aliq. PIS
        item.pis ?? null,                          // PIS R$
        item.aliquota_cofins ?? null,              // Aliq. COFINS
        item.cofins ?? null,                       // COFINS R$
        item.cofins ?? null,                       // COFINS a Recolher
        item.taxa_siscomex ?? null,                // Tx Siscomex
        item.base_icms ?? null,                    // Base ICMS R$
        item.aliquota_icms ?? null,                // Aliq. ICMS
        reducaoIcms,                               // % Redução ICMS
        aliqReduzida,                              // Aliq. reduzida ICMS
        valorIcmsBaseReduzida,                     // Valor do ICMS base reduzida
        item.icms ?? null,                         // ICMS R$
        item.marinha_mercante ?? null,             // AFRMM
    ];
}

function estilizarLinhaComoDados(row: ExcelJS.Row) {
    row.height = 18;
    for (let c = 1; c <= CABECALHO_ITENS.length; c++) {
        const cell = row.getCell(c);
        cell.font = FONTE_PADRAO as Font;
        cell.alignment = ALIGN_MIDDLE as Alignment; // sem wrap
        cell.border = BORDA_PRETA as Borders;
    }
}

function addTopoPlanilha(
    ws: ExcelJS.Worksheet,
    processo: string,
    dadosGrupo: any[],
    clientesSelecionados: ClienteSelecionado[],
    inicioVigencia: string | null,
    fimVigencia: string | null
): number {
    const primeiro = dadosGrupo[0] || {};
    const refs = Array.from(
        new Set(
            (dadosGrupo || [])
                .flatMap((x: any) => Array.isArray(x.referencias) ? x.referencias : [])
                .filter(Boolean)
        )
    );

    const topo: (string | number)[][] = [
        ['Processo:', processo ?? ''],
        ['Cliente:', primeiro.cliente ?? ''],
        ['Ref. Cliente:', refs.join('; ') || ''],
        ['Taxa DI:', primeiro.taxa_vmle ?? ''],
        ['Número DI:', primeiro.nr_di ?? ''],
    ];
    if (clientesSelecionados?.length) {
        topo.push(['Clientes:', clientesSelecionados.map(c => c.value).join(', ')]);
    }
    if (inicioVigencia && fimVigencia) {
        topo.push(['Período:', `${inicioVigencia} - ${fimVigencia}`]);
    }

    topo.forEach((arr) => {
        const r = ws.addRow(arr);

        const labelCell = r.getCell(1);
        labelCell.font = FONTE_HEADER as Font;
        labelCell.fill = PREENCHIMENTO_HEADER as Fill;
        labelCell.border = BORDA_PRETA as Borders;
        labelCell.alignment = ALIGN_MIDDLE as Alignment;

        const valueCell = r.getCell(2);
        valueCell.font = FONTE_PADRAO as Font;
        valueCell.border = BORDA_PRETA as Borders;
        valueCell.alignment = ALIGN_MIDDLE_LEFT as Alignment;
    });

    ws.addRow(['']);

    return ws.lastRow ? ws.lastRow.number + 1 : 1;
}

function estilizarHeader(row: ExcelJS.Row) {
    row.height = 20;
    row.eachCell((cell) => {
        cell.font = FONTE_HEADER as Font;
        cell.fill = PREENCHIMENTO_HEADER as Fill;
        cell.border = BORDA_PRETA as Borders;
        cell.alignment = ALIGN_MIDDLE as Alignment;
    });
}

export async function generateDraftDachserSpreadsheet(
    data: RelatorioLaItem[],
    processo: string | undefined,
    clientes: ClienteSelecionado[],
    inicioVigencia: string | null,
    fimVigencia: string | null
) {
    if (!Array.isArray(data) || data.length === 0) return;

    const wb = new ExcelJS.Workbook();

    const grupos = new Map<string, any[]>();
    data.forEach((d: any) => {
        const key = processo ?? d.processo ?? 'SEM-PROCESSO';
        const arr = grupos.get(key) ?? [];
        arr.push(d);
        grupos.set(key, arr);
    });

    grupos.forEach((itens, proc) => {
        const nomeAba = normalizarNomeAba(`Draft Dachser - ${proc}`);
        const ws = wb.addWorksheet(nomeAba);

        configurarLargurasMinimas(ws);

        addTopoPlanilha(ws, proc as string, itens, clientes, inicioVigencia, fimVigencia);

        const headerRow = ws.addRow([...CABECALHO_ITENS]);
        estilizarHeader(headerRow);

        itens.forEach((item) => {
            const row = ws.addRow(mapItemParaLinha(item));
            estilizarLinhaComoDados(row);
        });

        ws.views = [{ state: 'frozen', ySplit: headerRow.number }];

        autoFitColumns(ws, CABECALHO_ITENS.length);
    });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob(
        [buffer],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    );
    saveAs(blob, 'PlanilhaLaNova.xlsx');
}
